import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import './App.css';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import CustomerList from './components/CustomerList';
import CustomerForm from './components/CustomerForm';
import CollectionEntryList from './components/CollectionEntryList';
import CollectionEntryForm from './components/CollectionEntryForm';
import ReportView from './components/ReportView';
import Summary from './components/Summary';
import SalespersonSummary from './components/SalespersonSummary';
import PasswordChange from './components/PasswordChange';
import UserManagement from './components/UserManagement';
import Profile from './components/Profile';
import SecurityScore from './components/SecurityScore';
import SystemBackup from './components/SystemBackup';
import ARAgingDetails from './components/ARAgingDetails';
import api from './utils/api';
import { getRoleDisplayName, isAllBranchesRole } from './utils/auth';
import { ToastProvider } from './components/ToastContext';

// SVG Icon components
const Icons = {
  dashboard: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  customers: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  collection: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <line x1="2" y1="10" x2="22" y2="10" />
    </svg>
  ),
  report: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  ),
  upload: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  ),
  sync: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  ),
  profile: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  users: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  refresh: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  ),
  shield: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  backup: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </svg>
  ),
  logout: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  ),
  menu: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  )
};

function NavItem({ to, icon, label, active }) {
  return (
    <Link to={to} className={`nav-item ${active ? 'active' : ''}`}>
      <span className="nav-icon">{icon}</span>
      <span className="nav-label">{label}</span>
    </Link>
  );
}

function Sidebar({ user, onLogout, isOpen, onClose }) {
  const location = useLocation();
  const currentPath = location.pathname;

  const isActive = (path) => {
    if (path === '/dashboard') return currentPath === '/dashboard' || currentPath === '/';
    return currentPath.startsWith(path);
  };

  return (
    <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
      <div className="sidebar-brand">
        <button className="sidebar-close" onClick={onClose} aria-label="Close menu">
          ×
        </button>
        <img src="/new-logo.jpeg" alt="Collection Report System" className="sidebar-logo" />
        <div className="sidebar-brand-text">
          <span className="brand-title">Collection Report System</span>
        </div>
      </div>

      <nav className="sidebar-nav">
        <div className="nav-section">
          <span className="nav-section-title">Main</span>
          <NavItem to="/dashboard" icon={Icons.dashboard} label="Dashboard" active={isActive('/dashboard')} />
          <NavItem to="/customers" icon={Icons.customers} label="Customers" active={isActive('/customers')} />
          <NavItem to="/reports" icon={Icons.report} label="Summary Reports" active={isActive('/reports')} />
          <NavItem to="/summarybranch" icon={Icons.report} label="Summary Branch" active={isActive('/summarybranch')} />
          <NavItem to="/salesperson-summary" icon={Icons.users} label="Salesperson Summary" active={isActive('/salesperson-summary')} />
          <NavItem to="/agingreports" icon={Icons.collection} label="Aged Accounts Receivable" active={isActive('/agingreports')} />
          <NavItem to="/ara-aging-details" icon={Icons.collection} label="AR Aging Details" active={isActive('/ara-aging-details')} />
        </div>

        {user?.role === 'superadmin' && (
          <div className="nav-section">
            <span className="nav-section-title">Administration</span>
            <NavItem to="/users" icon={Icons.users} label="Users" active={isActive('/users')} />
            <NavItem to="/security-score" icon={Icons.shield} label="Security Score" active={isActive('/security-score')} />
            <NavItem to="/system-backup" icon={Icons.backup} label="System Backup" active={isActive('/system-backup')} />
          </div>
        )}

        <div className="nav-section">
          <span className="nav-section-title">Account</span>
          <NavItem to="/profile" icon={Icons.profile} label="Profile" active={isActive('/profile')} />
        </div>
      </nav>

      <div className="sidebar-footer">
        <div className="user-mini-profile">
          <div className="user-avatar">
            {(user?.full_name || user?.username || 'A').charAt(0).toUpperCase()}
          </div>
          <div className="user-info">
            <span className="user-name">{user?.full_name || user?.username || 'Admin'}</span>
            <span className="user-role">{getRoleDisplayName(user?.role)}</span>
          </div>
        </div>
        <button className="sidebar-logout" onClick={onLogout}>
          {Icons.logout}
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
}

function TopHeader({ user, onMenuToggle, onChangePassword, onLogout }) {
  const roleDisplayName = getRoleDisplayName(user?.role);

  return (
    <header className="top-header">
      <div className="header-left">
        <button className="mobile-menu-btn" onClick={onMenuToggle} aria-label="Open menu">
          {Icons.menu}
        </button>
        <h2 className="header-greeting">Welcome back, {user?.full_name || user?.username || 'admin'}</h2>
      </div>
      <div className="header-right">
        <span className="header-role">{roleDisplayName}</span>
        <div className="header-divider" />
        <button
          className="header-action-btn"
          onClick={onChangePassword}
          style={{ cursor: 'pointer' }}
        >
          Change Password
        </button>
        <button
          className="header-action-btn logout"
          onClick={onLogout}
          style={{ cursor: 'pointer' }}
        >
          Logout
        </button>
      </div>
    </header>
  );
}

function App() {
  // Initialize user from localStorage synchronously to prevent redirect on reload
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem('crs_user');
    const storedToken = localStorage.getItem('crs_token');
    if (storedUser && storedToken) {
      try {
        return JSON.parse(storedUser);
      } catch {
        return null;
      }
    }
    return null;
  });
  const [selectedCompany, setSelectedCompany] = useState(() => {
    const storedCompany = localStorage.getItem('crs_company');
    if (storedCompany) {
      try {
        return JSON.parse(storedCompany);
      } catch {
        return null;
      }
    }
    return null;
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  useEffect(() => {
    // Sync user state if it changed externally (e.g., login)
    const storedUser = localStorage.getItem('crs_user');
    const storedToken = localStorage.getItem('crs_token');
    if (storedUser && storedToken && !user) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        // ignore
      }
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchCompanies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');

      if (!selectedCompany) {
        let companyToSelect = null;
        if (response.data.length > 0) {
          // If user has single-company role, auto-select their company
          if (user?.company_id && !isAllBranchesRole(user.role)) {
            companyToSelect = response.data.find(c => c.id === user.company_id) || response.data[0];
          }
          // All-branches users default to "All Branches" (null) to see aggregate data
          setSelectedCompany(companyToSelect);
          if (companyToSelect) {
            localStorage.setItem('crs_company', JSON.stringify(companyToSelect));
          } else {
            localStorage.removeItem('crs_company');
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch companies:', err);
    }
  };

  const handleLogin = (data) => {
    localStorage.setItem('crs_token', data.token);
    localStorage.setItem('crs_user', JSON.stringify(data.user));
    setUser(data.user);
  };

  const handleLogout = () => {
    localStorage.removeItem('crs_token');
    localStorage.removeItem('crs_user');
    localStorage.removeItem('crs_company');
    setUser(null);
    setSelectedCompany(null);
  };

  const handlePasswordChangeClick = () => {
    setShowPasswordModal(true);
  };

  return (
    <ToastProvider>
      <Router>
        <div className="app">
          {!user ? (
            <Routes>
              <Route path="/login" element={<Login onLogin={handleLogin} />} />
              <Route path="*" element={<Navigate to="/login" />} />
            </Routes>
          ) : (
            <>
              {showPasswordModal && (
                <PasswordChange onClose={() => setShowPasswordModal(false)} />
              )}
              <div className="app-layout">
              <Sidebar
                user={user}
                onLogout={handleLogout}
                isOpen={sidebarOpen}
              onClose={() => setSidebarOpen(false)}
            />
            {sidebarOpen && (
              <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
            )}
            <div className="main-wrapper">
              <TopHeader
                user={user}
                onMenuToggle={() => setSidebarOpen(true)}
                onChangePassword={handlePasswordChangeClick}
                onLogout={handleLogout}
              />
              <main className="main-content">
                <Routes>
                  <Route path="/dashboard" element={<Dashboard companyId={selectedCompany?.id} />} />
                  <Route path="/summarybranch" element={<Summary companyId={selectedCompany?.id} />} />
                  <Route path="/salesperson-summary" element={<SalespersonSummary companyId={selectedCompany?.id} />} />
                  <Route path="/customers" element={<CustomerList companyId={selectedCompany?.id} />} />
                  <Route path="/customers/new" element={<CustomerForm companyId={selectedCompany?.id} />} />
                  <Route path="/customers/edit/:id" element={<CustomerForm companyId={selectedCompany?.id} />} />
                  <Route path="/agingreports" element={<CollectionEntryList companyId={selectedCompany?.id} />} />
                  <Route path="/agingreports/new" element={<CollectionEntryForm />} />
                  <Route path="/agingreports/edit/:id" element={<CollectionEntryForm />} />
                  <Route path="/reports" element={<ReportView companyId={selectedCompany?.id} />} />
                  <Route path="/ara-aging-details" element={<ARAgingDetails companyId={selectedCompany?.id} />} />
                  {user?.role === 'superadmin' && (
                    <>
                      <Route path="/users" element={<UserManagement />} />
                      <Route path="/security-score" element={<SecurityScore />} />
                      <Route path="/system-backup" element={<SystemBackup />} />
                    </>
                  )}
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/login" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/" element={<Navigate to="/dashboard" />} />
                </Routes>
              </main>
            </div>
          </div>
          </>
        )}
      </div>
    </Router>
    </ToastProvider>
  );
}

export default App;
